import { PartnerInfos, Project } from "types";
import { CopiableInfosSection } from "./CopiableInfosSection";
import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { AddPartnerToProjectModal } from "@components/modals/AddPartnerToProjectModal";
import { PLANNING_SECTIONS } from "@utils/planning_constants";

export const PartnersSection = ({
  project,
  setProject,
  readOnly = false,
}: {
  project: Project;
  setProject?: (project: Project) => void;
  readOnly?: boolean;
}) => {
  const [expandedPartners, setExpandedPartners] = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const handleSelectPartner = (partner: PartnerInfos) => {
    if (!setProject) return;
    if (project.infos.partners.some((p) => p.id === partner.id)) return;
    setProject({
      ...project,
      infos: { ...project.infos, partners: [...project.infos.partners, partner] },
    });
  };

  const handleRemovePartner = (partnerId: string) => {
    if (!setProject) return;
    setProject({
      ...project,
      infos: {
        ...project.infos,
        partners: project.infos.partners.filter((p) => p.id !== partnerId),
      },
    });
  };

  const nonSpecialtyPartners = project.infos.partners.filter(
    (p) => !PLANNING_SECTIONS.some((s) => s.key === p.company_type),
  );

  return (
    <div className="flex flex-col gap-1 w-500">
      {showAddModal && (
        <AddPartnerToProjectModal
          onClose={() => setShowAddModal(false)}
          onSelect={(partner) => {
            handleSelectPartner(partner);
            setShowAddModal(false);
          }}
          existingPartnerIds={project.infos.partners.map((p) => p.id)}
        />
      )}

      {/* Specialty rows — same as in create/modify modals */}
      {PLANNING_SECTIONS.map((section) => {
        const assigned = project.infos.partners.find(
          (p) => p.company_type === section.key,
        );
        return (
          <div
            key={section.key}
            className={`flex flex-row items-center justify-between px-4 py-2.5 rounded-lg border transition-colors ${
              assigned
                ? "bg-white border-neutral-200"
                : readOnly
                  ? "bg-neutral-50 border-neutral-200"
                  : "bg-neutral-50 border-neutral-200 cursor-pointer hover:bg-neutral-100"
            }`}
            onClick={!assigned && !readOnly ? () => setShowAddModal(true) : undefined}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              <div
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${assigned ? section.cellColor : "bg-neutral-300"}`}
              />
              <div className="flex flex-col">
                <div
                  className={`text-h3 font-medium ${assigned ? "text-black" : "text-neutral-400"}`}
                >
                  {section.label}
                </div>
                {assigned && (
                  <div className="text-h4 text-neutral-400">
                    {assigned.company_name || assigned.display_name}
                  </div>
                )}
              </div>
            </div>
            {assigned && !readOnly ? (
              <SecondaryButton
                danger={true}
                variant="small"
                onClick={() => handleRemovePartner(assigned.id)}
              >
                Retirer
              </SecondaryButton>
            ) : !assigned && !readOnly ? (
              <Icon1 name="plus" size={14} color="grey" />
            ) : null}
          </div>
        );
      })}

      {/* Non-specialty partners with expandable contact details */}
      {nonSpecialtyPartners.map((partner, index) => (
        <div key={index} className="flex flex-col mt-1">
          <div
            className="flex flex-row justify-between items-center px-4 py-2 rounded-lg bg-white shadow-md cursor-pointer"
            onClick={() => {
              if (expandedPartners.includes(partner.id)) {
                setExpandedPartners(expandedPartners.filter((id) => id !== partner.id));
              } else {
                setExpandedPartners([...expandedPartners, partner.id]);
              }
            }}
          >
            <div className="flex flex-col w-250">
              <div className="text-h2 text-black font-medium">
                {partner.company_name || ""}
              </div>
              <div className="text-h3 text-neutral-400 font-medium">{partner.role}</div>
            </div>
            <Icon1
              rotation={expandedPartners.includes(partner.id) ? 180 : 0}
              name="down"
              size={15}
            />
          </div>
          {expandedPartners.includes(partner.id) && (
            <div className="flex flex-col mt-2 rounded-lg p-4 gap-4">
              <CopiableInfosSection
                width="w-200"
                options={[
                  { label: "Tel entreprise", value: partner.company_phone || "" },
                  { label: "Email entreprise", value: partner.company_email || "" },
                  { label: "Nom complet", value: partner.display_name || "" },
                  { label: "Email", value: partner.email || "" },
                  { label: "Tel", value: partner.phone_number || "" },
                  { label: "Adresse", value: partner.company_address || "" },
                  { label: "Code postal", value: partner.company_postal || "" },
                ]}
              />
              {!readOnly && (
                <SecondaryButton
                  danger={true}
                  variant="small"
                  onClick={() => handleRemovePartner(partner.id)}
                >
                  Retirer
                </SecondaryButton>
              )}
            </div>
          )}
        </div>
      ))}

      {!readOnly && (
        <SecondaryButton
          startIcon={<Icon1 name="plus" size={16} />}
          label="Ajouter un intervenant"
          variant="large"
          onClick={() => setShowAddModal(true)}
        />
      )}
    </div>
  );
};
